import React, { useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { Button } from 'components/Button/Button';
import { CloseButton } from 'components/CloseButton/CloseButton';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';
import { AssessmentSubmissionState } from 'features/assessments/types';
import { CloseRounded } from '@mui/icons-material';

interface AssessmentSubmitDialogContent {
  title: string;
  body: string;
  primaryLabel: string;
  secondaryLabel?: string;
}

const DIALOG_CONTENT: Record<AssessmentSubmissionState, AssessmentSubmitDialogContent> = {
  'required-skipped': {
    title: 'You Skipped Required Questions',
    body: 'You must complete these questions before submitting your assessment.',
    primaryLabel: 'Finish Questions',
  },
  'optional-skipped': {
    title: 'You Have Skipped Questions',
    body:
      'Answering these questions helps us match care to you. ' +
      'Please complete these questions before submitting your assessment.',
    primaryLabel: 'Finish Questions',
    secondaryLabel: 'Submit',
  },
  completed: {
    title: 'You Finished Your Assessment',
    body:
      'All questions are completed, submit your assessment ' +
      'now by clicking on the button below.',
    primaryLabel: 'Submit Assessment',
    secondaryLabel: 'Go Back',
  },
};

interface AssessmentSubmitDialogProps {
  open: boolean;
  variant: AssessmentSubmissionState;
  onClose: () => void;
  onPrimaryAction: () => void;
  onSecondaryAction?: () => void;
}

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
}));

const IconButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingInline: theme.spacing(2),
  paddingTop: theme.spacing(2),
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const StyledContentStack = styled(Stack)(({ theme }) => ({
  textAlign: 'left',
  padding: theme.spacing(0, 3),
  gap: theme.spacing(2),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(5, 4, 4, 4),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 6),
}));

export const AssessmentSubmitDialog = ({
  open,
  variant,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
}: AssessmentSubmitDialogProps) => {
  const { isAlertOpen } = useLayoutElements();
  const { title, body, primaryLabel, secondaryLabel } = DIALOG_CONTENT[variant];

  const handlePrimaryClick = useCallback(() => {
    onClose();
    onPrimaryAction();
  }, [onClose, onPrimaryAction]);

  const handleSecondaryClick = useCallback(() => {
    onClose();
    onSecondaryAction?.();
  }, [onClose, onSecondaryAction]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="assessment-submit-dialog__title"
      aria-describedby="assessment-submit-dialog__body"
      disableEscapeKeyDown={isAlertOpen}
      disableEnforceFocus
      PaperProps={{
        tabIndex: 0,
      }}
    >
      <StyledDialogContent>
        <IconButtonContainer>
          <StyledIconButton
            id="assessment-submit-dialog__close-button"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <CloseRounded />
          </StyledIconButton>
        </IconButtonContainer>
        <StyledContentStack>
          <Typography variant="title2" lineHeight="36px" id="assessment-submit-dialog__title">
            {title}
          </Typography>
          <Typography variant="body1" id="assessment-submit-dialog__body">
            {body}
          </Typography>
        </StyledContentStack>
      </StyledDialogContent>
      <StyledDialogActions disableSpacing>
        <StyledButton
          id="assessment-submit-dialog__primary-button"
          variant="primary"
          onClick={handlePrimaryClick}
        >
          {primaryLabel}
        </StyledButton>
        {secondaryLabel && (
          <StyledButton
            id="assessment-submit-dialog__secondary-button"
            variant="text"
            onClick={handleSecondaryClick}
          >
            {secondaryLabel}
          </StyledButton>
        )}
      </StyledDialogActions>
    </Dialog>
  );
};
